import type {
  AtualizacoesObjetoTabletop,
  CenaTabletop,
  GeradorIdTabletop,
  NovoObjetoTabletop,
  ObjetoTabletop,
  TipoObjetoTabletop,
  TransformacaoTabletop,
  VetorTabletop,
} from './tipos'
import { moverVeiculoComApoiados } from './apoioVeiculo'

export const TRANSFORMACAO_TABLETOP_PADRAO: TransformacaoTabletop = Object.freeze({
  posicao: Object.freeze([0, 0, 0]) as VetorTabletop,
  rotacao: Object.freeze([0, 0, 0]) as VetorTabletop,
  escala: Object.freeze([1, 1, 1]) as VetorTabletop,
})

export const CENA_TABLETOP_VAZIA: CenaTabletop = Object.freeze({
  versao: 2,
  nome: 'Novo table top',
  objetos: Object.freeze([]),
})

const NOMES_PADRAO: Readonly<Record<TipoObjetoTabletop, string>> = Object.freeze({
  carro: 'Carro',
  suv: 'SUV',
  moto: 'Moto',
  caminhao: 'Caminhão',
  onibus: 'Ônibus',
  caminhaoBombeiros: 'Caminhão de Bombeiros (ABT)',
  resgate: 'Resgate (ambulância)',
  bombeiro: 'Bombeiro (EPI Incêndio)',
  bombeiroMedico: 'Bombeiro Médico (APH)',
  arvore: 'Árvore',
  poste: 'Poste',
  defensa: 'Defensa',
  cone: 'Cone',
  bloco: 'Bloco',
  vitima: 'Vítima',
  vidro: 'Vidro',
  detritosVidro: 'Detritos de vidro',
  calco: 'Calço escalonado',
  cunha: 'Cunha de resgate',
  vstrut: 'V-Strut',
  fitaCatraca: 'Fita catraca',
  cobertor: 'Cobertor maleável',
  lagrima: 'Escudo lágrima',
  prancha: 'Prancha rígida',
  muro: 'Muro',
  derramamento: 'Mancha de óleo',
  caboEletrico: 'Cabo energizado caído',
  areaUsa: 'Área Livre USA (Suporte Avançado)',
  manchaSangue: 'Mancha de sangue',
  extintorPQS: 'Extintor PQS',
  lonaFerramentas: 'Lona de ferramentas',
  mochilaTrauma: 'Mochila de trauma (APH)',
  fogo: 'Fogo / Princípio de incêndio',
})

const copiarVetor = (vetor: VetorTabletop): VetorTabletop => [vetor[0], vetor[1], vetor[2]]

export const copiarTransformacao = (
  transformacao: TransformacaoTabletop,
): TransformacaoTabletop => ({
  posicao: copiarVetor(transformacao.posicao),
  rotacao: copiarVetor(transformacao.rotacao),
  escala: copiarVetor(transformacao.escala),
})

export const copiarObjetoTabletop = (objeto: ObjetoTabletop): ObjetoTabletop => ({
  id: objeto.id,
  tipo: objeto.tipo,
  nome: objeto.nome,
  transformacao: copiarTransformacao(objeto.transformacao),
  ...(objeto.cor === undefined ? {} : { cor: objeto.cor }),
  ...(objeto.bloqueado === undefined ? {} : { bloqueado: objeto.bloqueado }),
  ...(objeto.oculto === undefined ? {} : { oculto: objeto.oculto }),
  ...(objeto.veiculo === undefined
    ? {}
    : {
        veiculo: {
          ...objeto.veiculo,
          estadosPecas: { ...objeto.veiculo.estadosPecas },
          vidros: { ...objeto.veiculo.vidros },
          protecoes: { ...objeto.veiculo.protecoes },
          ...(objeto.veiculo.dano ? { dano: { ...objeto.veiculo.dano } } : {}),
          ...(objeto.veiculo.deformacao ? { deformacao: { ...objeto.veiculo.deformacao } } : {}),
          ...(objeto.veiculo.deformacoes ? { deformacoes: objeto.veiculo.deformacoes.map((d) => ({ ...d })) } : {}),
          ...(objeto.veiculo.cortes ? { cortes: objeto.veiculo.cortes.map((c) => ({ ...c })) } : {}),
          ...(objeto.veiculo.pecaSelecionada !== undefined ? { pecaSelecionada: objeto.veiculo.pecaSelecionada } : {}),
        },
      }),
  ...(objeto.vitima === undefined ? {} : { vitima: { ...objeto.vitima } }),
  ...(objeto.equipamento === undefined ? {} : { equipamento: { ...objeto.equipamento } }),
  ...(objeto.risco === undefined
    ? {}
    : {
        risco: {
          ...objeto.risco,
          ...(objeto.risco.materiaisAceitos ? { materiaisAceitos: [...objeto.risco.materiaisAceitos] } : {}),
        },
      }),
})

export const copiarCenaTabletop = (cena: CenaTabletop): CenaTabletop => ({
  versao: 2,
  nome: cena.nome,
  ...(cena.historiaInicial === undefined ? {} : { historiaInicial: cena.historiaInicial }),
  objetos: cena.objetos.map(copiarObjetoTabletop),
  ...(cena.acionamento
    ? {
        acionamento: {
          ...cena.acionamento,
          informacoes: cena.acionamento.informacoes.map((item) => ({ ...item })),
        },
      }
    : {}),
})

export function criarCenaVazia(nome = CENA_TABLETOP_VAZIA.nome): CenaTabletop {
  return { versao: 2, nome, objetos: [] }
}

const gerarIdComEntropiaNativa = (): string | undefined => {
  const criptografia = globalThis.crypto
  if (!criptografia) return undefined

  if (typeof criptografia.randomUUID === 'function') {
    return criptografia.randomUUID()
  }

  if (typeof criptografia.getRandomValues === 'function') {
    const valores = criptografia.getRandomValues(new Uint32Array(4))
    return `tabletop-${Array.from(valores, (valor) =>
      valor.toString(16).padStart(8, '0'),
    ).join('')}`
  }

  return undefined
}

const gerarIdPadrao: GeradorIdTabletop = () =>
  gerarIdComEntropiaNativa() ??
  `tabletop-${Date.now().toString(36)}-${Math.floor(Math.random() * 0x1_0000_0000)
    .toString(36)
    .padStart(7, '0')}`

const tornarIdUnico = (cena: CenaTabletop, idBase: string): string => {
  if (!cena.objetos.some((objeto) => objeto.id === idBase)) return idBase

  let sufixo = 2
  while (cena.objetos.some((objeto) => objeto.id === `${idBase}-${sufixo}`)) {
    sufixo += 1
  }
  return `${idBase}-${sufixo}`
}

const validarNovoId = (cena: CenaTabletop, id: string): void => {
  if (id.trim().length === 0) throw new Error('Não foi possível gerar um ID válido para o objeto.')
  if (cena.objetos.some((objeto) => objeto.id === id)) {
    throw new Error(`Já existe um objeto com o ID "${id}".`)
  }
}

export function adicionarObjeto(
  cena: CenaTabletop,
  novoObjeto: NovoObjetoTabletop,
  gerarId?: GeradorIdTabletop,
): CenaTabletop {
  const idGerado = (gerarId ?? gerarIdPadrao)()
  const id = gerarId === undefined ? tornarIdUnico(cena, idGerado) : idGerado
  validarNovoId(cena, id)
  const objeto: ObjetoTabletop = {
    id,
    tipo: novoObjeto.tipo,
    nome: novoObjeto.nome ?? NOMES_PADRAO[novoObjeto.tipo],
    transformacao: copiarTransformacao(
      novoObjeto.transformacao ?? TRANSFORMACAO_TABLETOP_PADRAO,
    ),
    ...(novoObjeto.cor === undefined ? {} : { cor: novoObjeto.cor }),
    ...(novoObjeto.veiculo === undefined ? {} : { veiculo: novoObjeto.veiculo }),
    ...(novoObjeto.vitima === undefined ? {} : { vitima: novoObjeto.vitima }),
    ...(novoObjeto.equipamento === undefined ? {} : { equipamento: novoObjeto.equipamento }),
    ...(novoObjeto.risco === undefined ? {} : { risco: novoObjeto.risco }),
  }

  return { ...cena, objetos: [...cena.objetos, objeto] }
}

export function atualizarTransformacao(
  cena: CenaTabletop,
  id: string,
  alteracoes: Partial<TransformacaoTabletop>,
): CenaTabletop {
  const objeto = cena.objetos.find((o) => o.id === id)
  if (objeto?.veiculo) {
    return moverVeiculoComApoiados(cena, id, alteracoes)
  }
  return atualizarObjeto(cena, id, { transformacao: alteracoes })
}

export function atualizarObjeto(
  cena: CenaTabletop,
  id: string,
  alteracoes: AtualizacoesObjetoTabletop,
): CenaTabletop {
  if (!cena.objetos.some((objeto) => objeto.id === id)) return cena

  return {
    ...cena,
    objetos: cena.objetos.map((objeto) => {
      if (objeto.id !== id) return objeto
      const transformacao = alteracoes.transformacao
        ? {
            posicao: copiarVetor(
              alteracoes.transformacao.posicao ?? objeto.transformacao.posicao,
            ),
            rotacao: copiarVetor(
              alteracoes.transformacao.rotacao ?? objeto.transformacao.rotacao,
            ),
            escala: copiarVetor(alteracoes.transformacao.escala ?? objeto.transformacao.escala),
          }
        : objeto.transformacao

      return {
        ...objeto,
        ...(alteracoes.nome === undefined ? {} : { nome: alteracoes.nome }),
        ...(alteracoes.cor === undefined ? {} : { cor: alteracoes.cor }),
        ...(alteracoes.bloqueado === undefined ? {} : { bloqueado: alteracoes.bloqueado }),
        ...(alteracoes.oculto === undefined ? {} : { oculto: alteracoes.oculto }),
        transformacao,
        ...(alteracoes.veiculo === undefined || objeto.veiculo === undefined
          ? {}
          : { veiculo: { ...objeto.veiculo, ...alteracoes.veiculo } }),
        ...(alteracoes.vitima === undefined || objeto.vitima === undefined
          ? {}
          : { vitima: { ...objeto.vitima, ...alteracoes.vitima } }),
        ...(alteracoes.equipamento === undefined || objeto.equipamento === undefined
          ? {}
          : { equipamento: { ...objeto.equipamento, ...alteracoes.equipamento } }),
        ...(alteracoes.risco === undefined ? {} : { risco: { ...objeto.risco, ...alteracoes.risco } }),
      }
    }),
  }
}

export function duplicarObjeto(
  cena: CenaTabletop,
  id: string,
  gerarId?: GeradorIdTabletop,
): CenaTabletop {
  const original = cena.objetos.find((objeto) => objeto.id === id)
  if (!original) throw new Error(`Não foi encontrado um objeto com o ID "${id}".`)

  return adicionarObjeto(
    cena,
    {
      tipo: original.tipo,
      nome: `${original.nome} (cópia)`,
      transformacao: original.transformacao,
      ...(original.cor === undefined ? {} : { cor: original.cor }),
      // uma vítima duplicada não herda o vínculo de âncora: cairia sobre a
      // mesma vaga que a original ocupa. Ela nasce solta, no chão, ao lado.
      ...(original.vitima === undefined
        ? {}
        : { vitima: { ...original.vitima, local: { tipo: 'solo', postura: 'decubitoDorsal' } } }),
      ...(original.veiculo === undefined ? {} : { veiculo: original.veiculo }),
      ...(original.equipamento === undefined ? {} : { equipamento: original.equipamento }),
      ...(original.risco === undefined ? {} : { risco: original.risco }),
    },
    gerarId,
  )
}

/**
 * Remove um objeto da cena.
 *
 * Se for um veículo, as vítimas vinculadas a ele não somem: passam para o
 * solo, na mesma posição do mundo em que estavam (ver `vitimas.ts`). Este
 * módulo é intencionalmente ignorante de física/geometria — quem resolve a
 * posição de mundo e chama `atualizarObjeto` para soltar a vítima é o
 * chamador (o hook de estado do Table top), não `removerObjeto`.
 */
export function removerObjeto(cena: CenaTabletop, id: string): CenaTabletop {
  if (!cena.objetos.some((objeto) => objeto.id === id)) return cena
  return { ...cena, objetos: cena.objetos.filter((objeto) => objeto.id !== id) }
}

export function renomearCena(cena: CenaTabletop, nome: string): CenaTabletop {
  return { ...cena, nome }
}

/**
 * Encaixa um ponto do chão numa grade quadrada (padrão 0,25 m): a peça
 * arrastada ou tocada pousa em posições redondas, sem exigir precisão de
 * milímetro no toque. Passe `passoM` maior/menor para grades mais grossas
 * ou finas; `null`/`0` desliga o encaixe e devolve o ponto como veio.
 */
export function encaixarPosicaoXZ(
  x: number,
  z: number,
  passoM: number | null = 0.25,
): readonly [number, number] {
  if (!passoM) return [x, z]
  return [Math.round(x / passoM) * passoM, Math.round(z / passoM) * passoM]
}

/**
 * Mesma ideia do encaixe de posição, mas para o giro em Y: prende o ângulo
 * nos múltiplos de `passoGraus` (padrão 15°) para girar uma peça no toque
 * parar em ângulos redondos em vez de fracionários.
 */
export function encaixarRotacaoGraus(graus: number, passoGraus: number | null = 15): number {
  if (!passoGraus) return graus
  return Math.round(graus / passoGraus) * passoGraus
}

